import { ReactNode, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Routes, Route, BrowserRouter, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';

import 'react-toastify/dist/ReactToastify.css';

import * as Pages from './pages';
import { store } from './lib';
import { theme } from './theme';
import { LayoutSuspense } from './components/layout/layout-suspense';

import { AppRoutes, WithAuth } from './routes';
import { createGlobalStyle } from 'styled-components';
import { YM_COUNTER } from './config';

import './lib/i18next';
import { TarifInfoPopup } from './components/tarif-info';
import { MainNav } from './components/main-nav';
import { SwitchVersion } from './components';

const YandexMetrikaCounter = () => {
  const location = useLocation();

  useEffect(() => {
    ym(YM_COUNTER, 'hit', location.pathname);
  }, [location.pathname]);

  return null;
};

const renderAuthRoute = (component: ReactNode) => <WithAuth>{component}</WithAuth>;

export const App = () => {
  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Provider store={store}>
          <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
            <LayoutSuspense>
              <BrowserRouter>
                <YandexMetrikaCounter />

                <SwitchVersion />

                <PageWrapper>
                  <MainNav />

                  <Routes>
                    {/**
                     * Auth zone
                     */}
                    <Route path={AppRoutes.MAIN.get()} element={renderAuthRoute(<Pages.MainPage />)} />
                    <Route path={AppRoutes.CAMPAIGN.get()} element={renderAuthRoute(<Pages.CampaignFormPage />)} />
                    <Route
                      path={AppRoutes.CAMPAIGN_STATISTICS.get()}
                      element={renderAuthRoute(<Pages.StatisticsPage />)}
                    />
                    <Route path={AppRoutes.COUPONS.get()} element={renderAuthRoute(<Pages.CouponsPage />)} />
                    <Route path={AppRoutes.ACCOUNT.get()} element={renderAuthRoute(<Pages.AccountPage />)} />
                    <Route path={AppRoutes.CODE.get()} element={renderAuthRoute(<Pages.CodePage />)} />

                    <Route path={AppRoutes.SIGN_IN.get()} element={<Pages.SignInPage />} />
                    <Route path={AppRoutes.EMBED.get()} element={<Pages.EmbedPage />} />
                    <Route path={AppRoutes.USER_WIDGET.get()} element={<Pages.UserWidgetPage />} />
                    <Route path={AppRoutes.USER_WIDGET_WIX.get()} element={<Pages.UserWidgetPage />} />
                  </Routes>
                </PageWrapper>
                <TarifInfoPopup />
              </BrowserRouter>
            </LayoutSuspense>
          </Sentry.ErrorBoundary>
        </Provider>
      </ThemeProvider>

      <div id="portals-container">
        <ToastContainer />
      </div>

      <GlobalStyle />
    </div>
  );
};

const GlobalStyle = createGlobalStyle`
  :root {
    --eg-doc-height: 100%;
  }

  .eg-widget-popup__root {
    z-index: 100001;
  }

  .eg-widget-popup {
    margin: 0;
    width: 100vw;
    height: var(--eg-doc-height);
    max-width: initial;
    max-height: calc(100vh - 32px);
  }

  @media screen and (min-width: 768px) {
    .eg-widget-popup {
      height: 667px;
      max-width: 960px !important;
    }
  }

  .lds-ring {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;
  }
  .lds-ring div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 64px;
    height: 64px;
    margin: 8px;
    border: 4px solid #fff;
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #fff transparent transparent transparent;
  }
  .lds-ring div:nth-child(1) {
    animation-delay: -0.45s;
  }
  .lds-ring div:nth-child(2) {
    animation-delay: -0.3s;
  }
  .lds-ring div:nth-child(3) {
    animation-delay: -0.15s;
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .insert-code {
    font-size: 12px;
    border-radius: 4px;
  }
`;

const PageWrapper = styled.div`
  display: flex;
  min-height: 100vh;
`;
